import axios from 'axios';
import appConfig from '../config/appConfig';

/**
 * Multas ([MULTAS-1410], ARQUITECTURA_ANALISIS.md §9 "Motivos configurables"): amercement-api.
 * Catalogo de motivos por comunidad, actas de todos los motivos (velocidad incluida), labrado manual con
 * evidencia, confirmar/descartar (emite el PDF y notifica) y descargos de los residentes.
 */
function headers(token, extra = {}) {
  return {
    'X-API-Key': appConfig.apiKey,
    'X-Authorization': token,
    'X-Application-Id': 'CASTLEBLACK',
    'X-Device-OS': 'BROWSER',
    Accept: 'application/json',
    ...extra
  };
}

export const RETRY_DELAY_MS = 700;

/**
 * Una lectura (GET) que falla con 5xx o sin respuesta se reintenta una vez: al
 * entrar a Multas el pool de conexiones del barrio en amercement todavía se
 * está abriendo y alguna consulta da 500 la primera vez.
 */
export const retryOnce = instance => error => {
  const config = error && error.config;
  const status = error && error.response && error.response.status;
  const retryable = !error.response || status >= 500;
  if (
    !config ||
    config.__kvRetried ||
    (config.method || '').toLowerCase() !== 'get' ||
    !retryable
  ) {
    return Promise.reject(error);
  }
  config.__kvRetried = true;
  return new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS)).then(() =>
    instance(config)
  );
};

function client(token, communityId) {
  const instance = axios.create({
    baseURL: `${appConfig.getApiBaseUrl()}/amercement/c/${encodeURIComponent(
      communityId
    )}`,
    headers: headers(token),
    timeout: 30000
  });
  instance.interceptors.response.use(null, retryOnce(instance));
  return instance;
}

const json = { headers: { 'Content-Type': 'application/json' } };

// ------------------------------------------------------------------ catalogo de motivos

/** { available, types } — available=false: la base de la comunidad todavia no tiene el catalogo. */
export function listTypes(token, communityId, onlyActive = false) {
  return client(token, communityId)
    .get('/infraction-types', { params: { onlyActive } })
    .then(r => r.data);
}

export function createType(token, communityId, body) {
  return client(token, communityId)
    .post('/infraction-types', body, json)
    .then(r => r.data);
}

export function updateType(token, communityId, id, body) {
  return client(token, communityId)
    .put(`/infraction-types/${id}`, body, json)
    .then(r => r.data);
}

/** Devuelve null si se borro, o el tipo desactivado si tenia actas. */
export function deleteType(token, communityId, id) {
  return client(token, communityId)
    .delete(`/infraction-types/${id}`)
    .then(r => (r.status === 204 ? null : r.data));
}

// ------------------------------------------------------------------ módulo por comunidad

/** { enabled }: si la comunidad tiene Multas (cualquier usuario logueado). */
export function getModule(token, communityId) {
  return client(token, communityId)
    .get('/module')
    .then(r => r.data);
}

/**
 * Solo staff de Keenvil (403 si no): { communityId, enabled, radarProvider,
 * radarCode, configured, updatedAt, updatedBy }.
 */
export function getModuleSettings(token, communityId) {
  return client(token, communityId)
    .get('/module-settings')
    .then(r => r.data);
}

/** { enabled, radarProvider: NONE | VIALSEG, radarCode } (solo staff). */
export function saveModuleSettings(token, communityId, body) {
  return client(token, communityId)
    .put('/module-settings', body, json)
    .then(r => r.data);
}

/**
 * Estado de la integración del radar (solo staff): { lastPhoto, photosToday,
 * errors: { unreadablePlate, unmatched, duplicated, batchErrors }, batchErrorsToday,
 * repeatedToday: [{ file, plate, occurredAt, location, originalFineId, photoRecovered }] }.
 */
export function getRadarStatus(token, communityId) {
  return client(token, communityId)
    .get('/radar-status')
    .then(r => r.data);
}

// ------------------------------------------------------------------ actas

/**
 * Page de actas. filters: { type, status, from, to, unitId, actNumber, page, size }
 * (todos opcionales).
 */
export function listActs(token, communityId, filters = {}) {
  const params = {};
  Object.keys(filters).forEach(k => {
    if (filters[k] !== '' && filters[k] != null) {
      params[k] = filters[k];
    }
  });
  return client(token, communityId)
    .get('/infractions', { params })
    .then(r => r.data);
}

/** Cantidad de actas con esos filtros (pide una sola fila y usa totalElements). */
export function countActs(token, communityId, filters = {}) {
  return listActs(token, communityId, { ...filters, page: 0, size: 1 }).then(
    page => (page && page.totalElements) || 0
  );
}

export function getAct(token, communityId, fineId) {
  return client(token, communityId)
    .get(`/infractions/${fineId}`)
    .then(r => r.data);
}

export function createAct(token, communityId, body) {
  return client(token, communityId)
    .post('/infractions', body, json)
    .then(r => r.data);
}

/**
 * Alta manual de un acta de velocidad (radar de mano). Solo administracion. body: { plate, speedKmh, speedLimitKmh,
 * occurredAt, location, unitId, notes }. Devuelve la multa (SpeedFineDtos.Fine).
 */
export function createSpeedFine(token, communityId, body) {
  return client(token, communityId)
    .post('/speed-fines', body, json)
    .then(r => r.data);
}

/** Sube una foto de evidencia (JPG o PNG). */
export function uploadEvidence(token, communityId, fineId, file) {
  const form = new FormData();
  form.append('file', file, file.name);
  return client(token, communityId)
    .post(`/infractions/${fineId}/evidence`, form)
    .then(r => r.data);
}

/** Confirma: calcula el monto, emite el acta en PDF y la notifica segun la configuracion. */
/**
 * Confirma y emite el acta. El aviso en la app sale siempre (si está activo);
 * el email solo a los elegidos (vacío = sin email).
 */
/**
 * expectedAmount: el importe que vio el admin. Si al emitir da otro, el back
 * responde 409 amount.changed y no emite (ver amountChangedError).
 */
export function confirmAct(token, communityId, fineId, emails, expectedAmount) {
  const body = { emails: emails || [] };
  if (expectedAmount != null) {
    body.expectedAmount = Number(expectedAmount);
  }
  return client(token, communityId)
    .post(`/speed-fines/${fineId}/confirm`, body, json)
    .then(r => r.data);
}

/** 409 amount.changed del confirm: { currentAmount, expectedAmount }; si no, null. */
export function amountChangedError(error) {
  const response = error && error.response;
  if (!response || response.status !== 409) {
    return null;
  }
  const data = response.data;
  const first = Array.isArray(data) ? data[0] : data;
  if (
    !first ||
    (first.code !== 'amount.changed' && first.currentAmount == null)
  ) {
    return null;
  }
  return {
    currentAmount: first.currentAmount,
    expectedAmount: first.expectedAmount
  };
}

/** Asigna (o cambia) la unidad de un acta abierta: el back recalcula el importe. */
export function reassignAct(
  token,
  communityId,
  fineId,
  unitId,
  reason,
  individualGroupId
) {
  return client(token, communityId)
    .post(
      `/speed-fines/${fineId}/reassign`,
      {
        unitId,
        individualGroupId: individualGroupId || null,
        reason: reason || null
      },
      json
    )
    .then(r => r.data);
}

/** Vuelve a correr el cruce de patentes sobre las actas en borrador del lote. */
export function matchBatch(token, communityId, batchId) {
  return client(token, communityId)
    .post(`/speed-fines/batches/${batchId}/match`)
    .then(r => r.data);
}

/**
 * Corrige la patente de un acta abierta. Si la unidad no la asignó la
 * administración, el cruce la vuelve a buscar.
 */
export function correctPlate(token, communityId, fineId, plate) {
  return client(token, communityId)
    .put(`/speed-fines/${fineId}/plate`, { plate }, json)
    .then(r => r.data);
}

/**
 * Advertencia o multa antes de confirmar (solo tipos configurables, no
 * velocidad). warning=false: se cobra; true: advertencia con importe 0.
 * Devuelve el acta con el importe recalculado.
 */
export function setActWarning(token, communityId, fineId, warning, reason) {
  return client(token, communityId)
    .post(`/speed-fines/${fineId}/warning`, { warning, reason }, json)
    .then(r => r.data);
}

/** category: UNCOLLECTIBLE, RADAR_ERROR, EXEMPT u OTHER (sin category, el back usa OTHER). */
export function dismissAct(token, communityId, fineId, reason, category) {
  return client(token, communityId)
    .post(
      `/speed-fines/${fineId}/dismiss`,
      { reason, category: category || null },
      json
    )
    .then(r => r.data);
}

/**
 * Desestima varias (hasta 100) en un pedido: { dismissed: [ids], failed: [{ fineId, error }] }.
 * Si el back todavía no lo tiene (404/405), rechaza con notAvailable para
 * hacerlo de a una.
 */
export function dismissActs(token, communityId, fineIds, reason, category) {
  return client(token, communityId)
    .post(
      '/speed-fines/dismiss',
      { fineIds, reason, category: category || null },
      json
    )
    .then(r => r.data)
    .catch(error => {
      const status = error && error.response && error.response.status;
      if (status === 404 || status === 405) {
        const notAvailable = new Error('dismiss en lote no disponible');
        notAvailable.notAvailable = true;
        throw notAvailable;
      }
      throw error;
    });
}

/** { pdfUrl (firmada 15 min), notificationStatus, ... } */
export function getActa(token, communityId, fineId) {
  return client(token, communityId)
    .get(`/speed-fines/${fineId}/acta`)
    .then(r => r.data);
}

/** Reenvía el acta: emails = solo a esos (de los destinatarios); sin emails = a todos. */
export function notifyAct(token, communityId, fineId, emails) {
  const body = emails && emails.length ? { emails } : undefined;
  return client(token, communityId)
    .post(`/speed-fines/${fineId}/notify`, body, body ? json : undefined)
    .then(r => r.data);
}

/**
 * A quién se mandaría el acta: { recipients: [{ accountId, name, email, profileTypes }],
 * notificationsEnabled, testMode }.
 */
export function getRecipients(token, communityId, fineId) {
  return client(token, communityId)
    .get(`/speed-fines/${fineId}/recipients`)
    .then(r => r.data);
}

/** ZIP con los PDF de las actas elegidas (hasta 100; las que no tienen PDF se omiten). */
export function downloadActasZip(token, communityId, fineIds) {
  return client(token, communityId)
    .post(
      '/speed-fines/actas/zip',
      { fineIds },
      { ...json, responseType: 'blob' }
    )
    .then(r => r.data);
}

/** Descargo cargado por la administración: texto y archivo opcional (source ADMIN). */
export function createAdminAppeal(token, communityId, fineId, text, file) {
  const form = new FormData();
  form.append('text', text);
  if (file) {
    form.append('file', file, file.name);
  }
  return client(token, communityId)
    .post(`/speed-fines/${fineId}/appeal`, form)
    .then(r => r.data);
}

/** Datos del nombre de una foto de VialSeg: { recognized, plate, speedKmh, speedLimitKmh, occurredAt, location }. */
export function getFilenameInfo(token, communityId, name) {
  return client(token, communityId)
    .get('/speed-fines/filename-info', { params: { name } })
    .then(r => r.data);
}

/** { discardedDays, issuedDays, deletionEnabled, maxRecidivismWindowDays } */
export function getRetention(token, communityId) {
  return client(token, communityId)
    .get('/speed-fines/retention')
    .then(r => r.data);
}

/** Qué borraría hoy la retención (no borra nada). */
export function previewRetention(token, communityId) {
  return client(token, communityId)
    .post('/speed-fines/retention/preview')
    .then(r => r.data);
}

// ------------------------------------------------------------------ reglas de velocidad y reincidencia

/**
 * { basePrice, basePriceName, matchToleranceMinutes, recidivismWindowDays,
 *   bands: [{ fromKmh, toKmh, factor }], recidivism: [{ fromCount, toCount, surchargePct }] }.
 * Rangos y precio base son solo de velocidad; la reincidencia la usan todos los
 * tipos que la tengan activa.
 */
export function getSpeedConfig(token, communityId) {
  return client(token, communityId)
    .get('/speed-fines/config')
    .then(r => r.data);
}

/** PUT reemplaza toda la configuración: mandar siempre el objeto completo. */
export function saveSpeedConfig(token, communityId, config) {
  return client(token, communityId)
    .put('/speed-fines/config', config, json)
    .then(r => r.data);
}

/**
 * Solo la reincidencia (ventana 1-365 días + escalones; [] = sin recargo). No
 * exige precio base ni rangos: sirve en comunidades sin velocidad configurada.
 */
export function saveRecidivism(token, communityId, body) {
  return client(token, communityId)
    .put('/speed-fines/config/recidivism', body, json)
    .then(r => r.data);
}

// ------------------------------------------------------------------ configuracion del acta

/** { issuerName, legalText, defenseDays, notifyProfileTypes, notificationsEnabled, hasLogo, updatedAt, updatedBy } */
export function getActaSettings(token, communityId) {
  return client(token, communityId)
    .get('/speed-fines/acta-settings')
    .then(r => r.data);
}

export function updateActaSettings(token, communityId, body) {
  return client(token, communityId)
    .put('/speed-fines/acta-settings', body, json)
    .then(r => r.data);
}

/** Logo del acta: PNG o JPG de hasta 1 MB. */
export function uploadActaLogo(token, communityId, file) {
  const form = new FormData();
  form.append('file', file, file.name);
  return client(token, communityId)
    .post('/speed-fines/acta-settings/logo', form)
    .then(r => r.data);
}

/** Foto de la entrada del barrio para el acta de velocidad: PNG o JPG de hasta 2 MB. */
export function uploadActaEntranceImage(token, communityId, file) {
  const form = new FormData();
  form.append('file', file, file.name);
  return client(token, communityId)
    .post('/speed-fines/acta-settings/entrance-image', form)
    .then(r => r.data);
}

/**
 * Imágenes por radar: { available, showRadarImage, hasEntranceImage,
 * images: [{ location, imageUrl, updatedAt }], knownLocations }.
 */
export function getRadarImages(token, communityId) {
  return client(token, communityId)
    .get('/speed-fines/acta-settings/radar-images')
    .then(r => r.data);
}

/** Imagen (PNG o JPG, hasta 2 MB) para uno o varios lugares de radar. */
export function uploadRadarImage(token, communityId, locations, file) {
  const form = new FormData();
  locations.forEach(location => form.append('location', location));
  form.append('file', file, file.name);
  return client(token, communityId)
    .post('/speed-fines/acta-settings/radar-images', form)
    .then(r => r.data);
}

export function deleteRadarImage(token, communityId, location) {
  return client(token, communityId)
    .delete('/speed-fines/acta-settings/radar-images', { params: { location } })
    .then(r => r.data);
}

// ------------------------------------------------------------------ descargos

export function listAppeals(token, communityId, status) {
  return client(token, communityId)
    .get('/speed-fines/appeals', { params: status ? { status } : {} })
    .then(r => r.data);
}

/**
 * Acepta el descargo y pasa el acta a otra unidad: anula la original y crea una
 * pendiente para la unidad nueva con la misma evidencia. { appeal, originalFine, newFine }.
 */
export function transferAppeal(
  token,
  communityId,
  appealId,
  { unitId, individualGroupId, response }
) {
  return client(token, communityId)
    .post(
      `/speed-fines/appeals/${appealId}/transfer`,
      { unitId, individualGroupId: individualGroupId || null, response },
      json
    )
    .then(r => r.data);
}

export function getAppeal(token, communityId, appealId) {
  return client(token, communityId)
    .get(`/speed-fines/appeals/${appealId}`)
    .then(r => r.data);
}

export function resolveAppeal(token, communityId, appealId, accept, response) {
  return client(token, communityId)
    .post(
      `/speed-fines/appeals/${appealId}/${accept ? 'accept' : 'reject'}`,
      { response },
      json
    )
    .then(r => r.data);
}

/**
 * Reabre un descargo resuelto (error de la administración): vuelve a pendiente.
 * Si estaba aceptado, el acta vuelve a confirmada. El motivo queda en el historial.
 */
export function reopenAppeal(token, communityId, appealId, reason) {
  return client(token, communityId)
    .post(`/speed-fines/appeals/${appealId}/reopen`, { reason }, json)
    .then(r => r.data);
}

// ------------------------------------------------------------------ unidades (community-api)

function communityClient(token, communityId) {
  return axios.create({
    baseURL: `${appConfig.getApiBaseUrl()}/community/c/${encodeURIComponent(
      communityId
    )}`,
    headers: headers(token),
    timeout: 20000
  });
}

/** Unidades que coinciden con el texto (para el autocompletado). */
export function searchUnits(token, communityId, text) {
  return communityClient(token, communityId)
    .get('/units', { params: { q: text, empty: false } })
    .then(r => (Array.isArray(r.data) ? r.data : r.data.content || []));
}

const unitLabels = {};

/** "UF 12 - Lote 34" de una unidad (con cache por pestaña del navegador). */
export function unitLabel(token, communityId, unitId) {
  const key = `${communityId}:${unitId}`;
  if (!unitId) {
    return Promise.resolve('-');
  }
  if (!unitLabels[key]) {
    unitLabels[key] = communityClient(token, communityId)
      .get(`/units/${unitId}/info`)
      .then(r => {
        const u = r.data && (r.data.unit || r.data);
        return u && (u.unitNumber || u.lotNumber)
          ? `UF ${u.unitNumber || '-'} - Lote ${u.lotNumber || '-'}`
          : `Unidad ${unitId}`;
      })
      .catch(() => `Unidad ${unitId}`);
  }
  return unitLabels[key];
}

/** Mensaje legible de un error del back ({ message }) o de red. */
/**
 * Texto del error para mostrar. amercement responde [{ title, detail }]
 * (KeenvilApiError); otros servicios, { message }.
 */
export function errorMessage(error) {
  const data = error && error.response && error.response.data;
  const first = Array.isArray(data) ? data[0] : data;
  if (first && (first.detail || first.message)) {
    return first.detail || first.message;
  }
  if (error && error.response && error.response.status === 403) {
    return 'No tenés permiso para esta operación.';
  }
  return 'No se pudo completar la operación. Probá de nuevo en unos minutos.';
}
